import { gql, useQuery } from "@apollo/client";
import { useFocusEffect } from "@react-navigation/native";
import { useCallback } from "react";
import { TouchableOpacity, View } from "react-native";
import { ActivityIndicator, Text } from "react-native-paper";
import { AvatarProfile, ScreenContainer, ScrollableContainer } from "../components";
import NotFound from "../components/NotFound";
import { formatDate } from "../utils/dateUtils";
import { CURRENT_MEMBER_FRAGMENT } from "./fragments";
import { ScreenProps } from "./types";

interface ItemRowProps extends Submission {
  isLast: boolean;
}

const ItemRow = ({ expirationDate, createdAt, submitter, isLast }: ItemRowProps) => {
  const { firstName, lastName } = submitter;
  return (
    <>
      <View className="flex-row px-6 py-4">
        <AvatarProfile
          {...{
            profilePhoto: submitter.profilePhoto,
            firstName,
            lastName,
            size: 40,
          }}
        />
        <View className="flex-1 pl-3">
          <View className="flex-row justify-between mb-2">
            <View className="flex-row items-center">
              <Text className="text-small font-400 text-ink-darkest">{submitter.name}</Text>
            </View>
            <Text className="text-small-tight font-400 text-ink-light">{formatDate(createdAt, "MMM D")}</Text>
          </View>
          <Text className="text-small font-700 text-ink-darkest mb-1">REVIEW: Deferred Compensation activity</Text>
          <Text className="text-small-tight font-400 text-ink-light">
            Please review this activity by {formatDate(expirationDate, "MMM Do")}
          </Text>
        </View>
      </View>
      {!isLast ? <View className="border-b border-sky" /> : <View className="border-b border-sky sm:border-0" />}
    </>
  );
};

export const QUERY = gql`
  query activityReviewListQuery {
    currentMember {
      ...CurrentMemberFragment
      pendingSubmissions: deferredCompSubmissionsToReview {
        id
        expirationDate
        createdAt
        submitter {
          id
          name
          firstName
          lastName
          profilePhoto
        }
      }
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
`;

interface Submission {
  id: string;
  createdAt: string;
  expirationDate: string;
  submitter: {
    name: string;
    firstName: string;
    lastName: string;
    profilePhoto: string;
  };
}
interface Query {
  currentMember: {
    pendingSubmissions: Submission[];
  };
}

const ActivityReviewListScreen = ({ navigation }: ScreenProps<"ActivityReviewList">) => {
  const { data, loading, refetch } = useQuery<Query>(QUERY);
  const pendingSubmissions = data?.currentMember.pendingSubmissions || [];

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [])
  );

  const navigateToActivity =
    ({ id }: Submission) =>
    () =>
      navigation.navigate("ActivityReview", { id });

  const navigateToDeferredComp = () => navigation.navigate("DeferredComp");

  if (loading) {
    return (
      <View className="flex-1 bg-sky-white items-center justify-center">
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <ScreenContainer
      testID="ActivityReviewListScreen"
      navigationTitle="Activity Review Requests"
      goBack={navigateToDeferredComp}
    >
      {pendingSubmissions.length === 0 ? (
        <View className="items-center justify-center flex-1">
          <NotFound title="No review requests" message="New requests will display here when received." icon="inbox" />
        </View>
      ) : (
        <ScrollableContainer>
          {pendingSubmissions.map((item, index) => (
            <TouchableOpacity onPress={navigateToActivity(item)} key={item.id} activeOpacity={0.7} testID={`item-${index}`}>
              <ItemRow {...item} isLast={pendingSubmissions.length - 1 === index} />
            </TouchableOpacity>
          ))}
        </ScrollableContainer>
      )}
    </ScreenContainer>
  );
};

export default ActivityReviewListScreen;
